import { useEffect, useState } from 'react';
import { businessApi } from '@/lib/api-paths';
import { useBusinessId } from '@/hooks/use-tenant-params';
import { Link } from 'react-router-dom';
import { Button, buttonVariants } from '@/components/ui/button';
import { EmptyState } from '@/components/empty-state';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { UpgradeGate } from '@/components/upgrade-gate';
import { readyPrimaryVariant } from '@/lib/primary-action';
import { locationRoutes } from '@/lib/route-map';

// =====================================================================
// DomainPanel — BYOD custom hostname management (Phase 6a).
//
// Loads GET /api/domains?locationId= and renders one of:
//   1. "not configured" notice — CF feature flags not set yet
//   2. Connect form — no domain attached
//   3. Domain card — domain attached, shows status + DNS instructions
//      when pending_verification
// =====================================================================

interface DnsRecord {
  type: string;
  name: string;
  value: string;
}

interface DomainRow {
  id: string;
  domainName: string;
  status: 'pending_verification' | 'active' | 'expired' | 'deleted';
  registrar: 'cloudflare' | 'external';
  isFreeTier: boolean;
}

interface DomainsResponse {
  configured: boolean;
  domains: DomainRow[];
  canFreeDomain?: boolean;
}

function errorCopy(code: string): string {
  switch (code) {
    case 'invalid_domain':
      return "That doesn't look like a valid domain. Enter something like yourbusiness.com.";
    case 'domain_taken':
      return 'That domain is already connected to another site.';
    // 'tier_not_allowed' is handled before this function is called — see
    // connect()'s gate check (WP1 Task 12).
    default:
      return 'Something went wrong. Please try again.';
  }
}

function registerErrorCopy(code: string): string {
  switch (code) {
    case 'free_domain_exists':
      return "You've already claimed your free domain.";
    case 'not_registrable':
      return "That domain isn't available. Try another name.";
    // 'tier_not_allowed' is handled before this function is called — see
    // claim()'s gate check (WP1 Task 12).
    case 'registration_failed':
      return "Registration couldn't complete. Try a different domain, or connect one you own.";
    default:
      return 'Something went wrong. Please try again.';
  }
}

interface DomainCandidate {
  name: string;
  pricing?: { registration_cost?: string };
}

function StatusBadge({ status }: { status: DomainRow['status'] }) {
  const variantMap: Record<
    DomainRow['status'],
    React.ComponentProps<typeof Badge>['variant']
  > = {
    active: 'success',
    pending_verification: 'warning',
    expired: 'muted',
    deleted: 'muted',
  };
  const labelMap: Record<DomainRow['status'], string> = {
    active: 'Active',
    pending_verification: 'Pending verification',
    expired: 'Expired',
    deleted: 'Removed',
  };
  return <Badge variant={variantMap[status]}>{labelMap[status]}</Badge>;
}

function DnsRow({ record }: { record: DnsRecord }) {
  return (
    <div className="grid grid-cols-[6rem_1fr] gap-x-4 gap-y-1 rounded-md bg-card p-3 text-sm border border-border-subtle">
      <span className="text-text-tertiary">Type</span>
      <code className="font-mono">{record.type}</code>
      <span className="text-text-tertiary">Name</span>
      <code className="break-all font-mono">{record.name}</code>
      <span className="text-text-tertiary">Value</span>
      <code className="break-all font-mono">{record.value}</code>
    </div>
  );
}

export function DomainPanel({
  siteId,
  primaryClaimed = false,
}: {
  siteId: string;
  /** True while the screen's header owns the single fill (Launch). */
  primaryClaimed?: boolean;
}) {
  // Canonical tenant URLs carry the Business id; these screens are only
  // ever rendered inside one, so it comes from the router, not a prop.
  const businessId = useBusinessId() ?? '';
  const [configured, setConfigured] = useState(true);
  const [domain, setDomain] = useState<DomainRow | null>(null);
  const [dns, setDns] = useState<DnsRecord[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gate, setGate] = useState<string | null>(null);
  // "Get your free domain" flow (every paid tier includes 1, Growth includes 3).
  const [q, setQ] = useState('');
  const [results, setResults] = useState<DomainCandidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [freeAvailable, setFreeAvailable] = useState(false);
  const [claiming, setClaiming] = useState<string | null>(null);

  async function load() {
    const res = await fetch(businessApi(businessId, `domains?locationId=${encodeURIComponent(siteId)}`));
    if (res.status === 401) {
      window.location.href = '/login';
      return;
    }
    const data = (await res.json()) as DomainsResponse;
    setConfigured(data.configured !== false);
    setFreeAvailable(data.canFreeDomain === true);
    const active = data.domains.find((d) => d.status !== 'deleted') ?? null;
    setDomain(active);
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  async function connect() {
    setBusy(true);
    setError(null);
    setGate(null);
    try {
      const res = await fetch(businessApi(businessId, 'domains'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ siteId, domainName: input }),
      });
      const data = (await res.json()) as {
        disabled?: boolean;
        error?: string;
        dns?: DnsRecord[];
        id?: string;
        status?: string;
      };
      if (data.disabled) {
        setConfigured(false);
        return;
      }
      if (!res.ok) {
        if (data.error === 'tier_not_allowed') {
          setGate('Custom domains need a paid plan.');
        } else {
          setError(errorCopy(data.error ?? ''));
        }
        return;
      }
      setDns(data.dns ?? []);
      setInput('');
      await load();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function search() {
    setSearching(true);
    setError(null);
    try {
      const res = await fetch(businessApi(businessId, `domains/search?q=${encodeURIComponent(q)}`));
      if (res.status === 401) {
        window.location.href = '/login';
        return;
      }
      const data = (await res.json()) as { disabled?: boolean; domains?: DomainCandidate[] };
      if (data.disabled || res.status === 403) {
        setFreeAvailable(false);
        return;
      }
      if (res.ok) setResults(data.domains ?? []);
    } catch {
      // leave results unchanged
    } finally {
      setSearching(false);
    }
  }

  async function claim(name: string) {
    const ok = window.confirm(
      `Register ${name} for free? This reserves the domain for your account and can't be undone.`,
    );
    if (!ok) return;
    setClaiming(name);
    setError(null);
    setGate(null);
    try {
      const res = await fetch(businessApi(businessId, 'domains/register'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ siteId, domainName: name }),
      });
      const data = (await res.json()) as { disabled?: boolean; error?: string };
      if (data.disabled) {
        setFreeAvailable(false);
        return;
      }
      if (!res.ok) {
        if (data.error === 'tier_not_allowed') {
          setGate('Free domains are included on every paid plan.');
        } else {
          setError(registerErrorCopy(data.error ?? ''));
        }
        return;
      }
      setResults([]);
      await load();
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setClaiming(null);
    }
  }

  async function check() {
    if (!domain) return;
    setBusy(true);
    try {
      const res = await fetch(businessApi(businessId, `domains/${domain.id}/verify`), { method: 'POST' });
      const data = (await res.json()) as { dns?: DnsRecord[]; disabled?: boolean };
      if (data.dns) setDns(data.dns);
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!domain) return;
    const ok = window.confirm(
      `Remove ${domain.domainName}? Your visitors will no longer reach this site on that address.`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      await fetch(businessApi(businessId, `domains/${domain.id}`), { method: 'DELETE' });
      setDns([]);
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="h-24 animate-pulse rounded-lg bg-muted" />
    );
  }

  if (!configured) {
    // "Check back soon." asked the owner to keep checking a screen that will
    // not change on its own (design audit T10/A12). This says what is actually
    // true — the hold-up is on our side, their site keeps working meanwhile —
    // and offers the one action that really moves it: telling us.
    return (
      <EmptyState
        size="compact"
        title="Domains aren't switched on yet"
        description="This one's on us — domain setup isn't running on your account yet. Your site stays live on its pageboss address until it is."
        action={
          <a
            href="mailto:support@pageboss.com?subject=Custom%20domains"
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            Email support
          </a>
        }
      />
    );
  }

  if (!domain) {
    return (
      <div className="space-y-4">
        {/* WP3b (D4, A7): "the domain panel's 'start' CTA also opens the
            wizard" — this panel keeps working as-is for direct visits;
            the guided wizard is an additional entry point over the same
            APIs below, not a replacement for them. */}
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border-subtle bg-surface-sunken px-4 py-3">
          <p className="text-sm text-text-secondary">
            Prefer a guided, step-by-step setup?
          </p>
          <Link
            to={locationRoutes.domainWizard(businessId, siteId)}
            className="shrink-0 text-sm font-medium text-brand-700 hover:text-brand-800 hover:underline"
          >
            Start guided setup →
          </Link>
        </div>

        {freeAvailable ? (
          <div className="space-y-4 rounded-lg border border-border-subtle bg-card p-6">
            <div>
              {/* B9: h2, not h3 — the Domain tab has an h1 (PageHeader) and nothing
                  between, so an h3 skipped a level. */}
              <h2 className="text-sm font-semibold text-foreground">Get a free domain</h2>
              <p className="mt-1 text-sm text-text-secondary">
                Included with every paid plan. Search for a name and claim it — we set up everything for you.
              </p>
            </div>
            <div className="flex gap-2">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="your business name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && q.trim().length >= 2 && !searching) {
                    void search();
                  }
                }}
              />
              {/* Design audit T8 (A11): same outline-until-actionable rule as
                  the DNS save on the Website tab — the Domain tab shares the
                  Website screen's header (Launch), so an idle, disabled filled
                  button here was a second primary that could never be pressed.
                  T14: and while Launch is actually on screen, this stays
                  outline even when it IS actionable. */}
              <Button
                type="button"
                variant={readyPrimaryVariant(q.trim().length >= 2, primaryClaimed)}
                disabled={searching || q.trim().length < 2}
                onClick={() => void search()}
              >
                Search
              </Button>
            </div>
            {results.length > 0 ? (
              <ul className="space-y-2">
                {results.map((r) => (
                  <li
                    key={r.name}
                    className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-surface-sunken px-3 py-2 text-sm"
                  >
                    <span className="flex items-center gap-2">
                      <code className="font-mono text-foreground">{r.name}</code>
                      <Badge variant="success">Free</Badge>
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={claiming !== null}
                      onClick={() => void claim(r.name)}
                    >
                      {claiming === r.name ? 'Claiming…' : 'Claim free'}
                    </Button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div className="space-y-4 rounded-lg border border-border-subtle bg-card p-6">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Connect your domain</h2>
          <p className="mt-1 text-sm text-text-secondary">
            Connect a domain you already own — like{' '}
            <code className="font-mono text-xs">yourbusiness.com</code>. You'll get DNS records to
            add at your registrar.
          </p>
        </div>
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="yourbusiness.com"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && input.trim().length > 0 && !busy) {
                void connect();
              }
            }}
          />
          {/* Same rule as Search above (design audit T8, A11). */}
          <Button
            type="button"
            variant={readyPrimaryVariant(input.trim().length > 0, primaryClaimed)}
            disabled={busy || input.trim().length === 0}
            onClick={() => void connect()}
          >
            Connect
          </Button>
        </div>
          {gate ? (
            <UpgradeGate capability={gate} planKey="pro" from={window.location.pathname} />
          ) : error ? (
            <p className="rounded-md bg-danger-bg px-3 py-2 text-sm text-danger-fg">{error}</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-lg border border-border-subtle bg-card p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1.5">
          <p className="font-medium text-foreground">{domain.domainName}</p>
          <StatusBadge status={domain.status} />
        </div>
        <Button
          type="button"
          variant="link"
          onClick={() => void remove()}
          disabled={busy}
          className="h-auto shrink-0 p-0 text-danger-fg"
        >
          Remove
        </Button>
      </div>

      {domain.status === 'pending_verification' ? (
        <div className="space-y-3 rounded-lg border border-warning-bg bg-warning-bg/40 p-4">
          <p className="text-sm text-text-secondary">
            Add these records at your domain's DNS provider, then click{' '}
            <strong>Check status</strong>.
          </p>
          <p className="text-xs text-text-tertiary">
            Connecting a root domain (like <code className="font-mono">acme.com</code>,
            without <code className="font-mono">www</code>)? Some DNS providers can't point a
            root domain with these records. If yours rejects them, use{' '}
            <code className="font-mono">www.{domain.domainName}</code> instead, or move your
            domain's DNS to Cloudflare (free) — that handles it automatically.
          </p>
          {dns.length === 0 ? (
            <p className="text-xs text-text-tertiary">
              Click <strong>Check status</strong> to load the DNS records for this domain.
            </p>
          ) : (
            <div className="space-y-2">
              {dns.map((r) => (
                <DnsRow key={`${r.type}-${r.name}`} record={r} />
              ))}
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => void check()}
          >
            Check status
          </Button>
        </div>
      ) : null}

      {domain.status === 'expired' ? (
        <div className="rounded-lg border border-border-subtle bg-surface-sunken p-4 text-sm text-text-secondary">
          This domain expired before verification completed. Remove it and try connecting again.
        </div>
      ) : null}
    </div>
  );
}
